import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import topicsVersicherungen from '@/assets/topics-versicherungen.webp';
import topicsAltersvorsorge from '@/assets/topics-altersvorsorge.webp';
import topicsInvestment from '@/assets/topics-investment.webp';
import topicsBeratung from '@/assets/topics-beratung.webp';

const topics = [
  {
    id: 1,
    title: 'Versicherungen',
    description: 'Versicherungen stellen das Fundament deiner langfristigen Finanzplanung dar. Welche für dich als Pharmazeut im Studium und anschließend im Berufsalltag essenziell sind, erfährst du hier!',
    image: topicsVersicherungen,
    href: '/versicherungen',
  },
  {
    id: 2,
    title: 'Altersvorsorge',
    description: 'Einen sorgenfreien Ruhestand wünschen wir uns alle. Wir zeigen dir, welche Zulagen und Förderungen dir zustehen und wie einfach langfristige Geldanlagen sein können.',
    image: topicsAltersvorsorge,
    href: '/altersvorsorge',
  },
  {
    id: 3,
    title: 'Investment',
    description: 'Ob eigene Immobilie oder Apotheke, große Projekte benötigen viel Kapital. Lass dein Geld für dich arbeiten und komm so schneller ans Ziel!',
    image: topicsInvestment,
    href: '/investment',
  },
  {
    id: 4,
    title: 'Individuelle Beratung',
    description: 'Wir unterstützen dich bei allen wichtigen finanziellen Entscheidungen. Immer passend zu deiner aktuellen Lebensphase. Unabhängig und kostenfrei!',
    image: topicsBeratung,
    href: '/beratung/individuell',
  },
];

const TopicsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section id="topics" className="relative py-20 md:py-28 bg-background" ref={ref}>
      <div className="container max-w-[1280px] px-6 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-14 md:mb-20"
        >
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-serif font-normal tracking-tight text-foreground">
            Hierbei unterstützen wir{' '}
            <span className="italic text-primary">dich</span>
          </h2>
        </motion.div>

        {/* Topics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {topics.map((topic, index) => (
            <motion.div
              key={topic.id}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.1 + index * 0.1, ease: [0.22, 1, 0.36, 1] }}
            >
              <Link
                to={topic.href}
                className="group block h-full bg-card rounded-2xl overflow-hidden shadow-[0_4px_20px_-4px_hsl(var(--foreground)/0.08)] hover:shadow-[0_12px_40px_-8px_hsl(var(--foreground)/0.15)] transition-all duration-500 hover:-translate-y-1"
              >
                {/* Image */}
                <div className="relative overflow-hidden aspect-[4/3]">
                  <img
                    src={topic.image}
                    alt={topic.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/[0.01] via-transparent to-transparent" />
                </div>

                {/* Content */}
                <div className="p-6 md:p-7">
                  {/* Title */}
                  <h3 className="text-lg md:text-xl font-serif font-normal text-foreground mb-3 tracking-tight">
                    {topic.title}
                  </h3>

                  {/* Description */}
                  <p className="text-sm leading-relaxed text-muted-foreground mb-5">
                    {topic.description}
                  </p>

                  {/* CTA Link */}
                  <span className="inline-flex items-center gap-2 text-sm font-medium text-primary transition-all duration-300 group-hover:gap-3">
                    Mehr erfahren
                    <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TopicsSection;
